/**
 * Locale-aware date formatting utilities.
 * Uses Intl.DateTimeFormat so month/day names follow each language's conventions.
 */

const LOCALE_MAP: Record<string, string> = {
  en: 'en-US', zh: 'zh-CN', hi: 'hi-IN', es: 'es-ES', ar: 'ar-SA',
  id: 'id-ID', ms: 'ms-MY', my: 'my-MM', fa: 'fa-AF', bo: 'bo-CN',
  am: 'am-ET', mn: 'mn-MN', lo: 'lo-LA',
};

export function getLocale(lang: string): string {
  return LOCALE_MAP[lang] || 'en-US';
}

/** "Jan 15" / "15 Jan" depending on locale */
export function formatDateShort(dateStr: string, lang: string): string {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString(getLocale(lang), { month: 'short', day: 'numeric' });
}

/** "Jan 15, 2026" / locale equivalent — short date with year */
export function formatDateShortYear(dateStr: string, lang: string): string {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString(getLocale(lang), { month: 'short', day: 'numeric', year: 'numeric' });
}

/** "Mon, Jan 15" / locale equivalent */
export function formatDateWithDay(dateStr: string, lang: string): string {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString(getLocale(lang), { weekday: 'short', month: 'short', day: 'numeric' });
}

/** Full month name for a 0-based month index: "January" / "Januari" */
export function formatMonthName(monthIndex: number, lang: string): string {
  const d = new Date(2026, monthIndex, 1);
  return d.toLocaleDateString(getLocale(lang), { month: 'long' });
}

/** Short month name: "Jan" / "Jan" */
export function formatMonthShort(monthIndex: number, lang: string): string {
  const d = new Date(2026, monthIndex, 1);
  return d.toLocaleDateString(getLocale(lang), { month: 'short' });
}

/** Narrow day header (Su/Mo or locale equivalent) for a 0-based day index (0=Sunday) */
export function formatDayHeader(dayIndex: number, lang: string): string {
  // Jan 4 2026 is a Sunday
  const d = new Date(2026, 0, 4 + dayIndex);
  return d.toLocaleDateString(getLocale(lang), { weekday: 'short' }).slice(0, 2);
}

/** Short day name from a date string: "Mon" / "Sen" */
export function formatDayShort(dateStr: string, lang: string): string {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString(getLocale(lang), { weekday: 'short' });
}

/** "Monday, January 15, 2026" / locale equivalent */
export function formatDateLong(dateStr: string, lang: string): string {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString(getLocale(lang), { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
}

/** Extract 1-based month number from a YYYY-MM-DD date string */
export function getMonthFromDate(dateStr: string): number {
  return parseInt(dateStr.slice(5, 7), 10);
}

/** Locale-aware date range: "1–5 January 2026" or "January 1, 2026 – February 3, 2026" */
export function formatDateRangeLocale(dateStart: string, dateEnd: string, lang: string): string {
  const locale = getLocale(lang);
  const start = new Date(dateStart + 'T00:00:00');
  const end = new Date(dateEnd + 'T00:00:00');
  const opts: Intl.DateTimeFormatOptions = { month: 'long', day: 'numeric', year: 'numeric' };

  if (start.getTime() === end.getTime()) {
    return start.toLocaleDateString(locale, opts);
  }

  if (dateStart.slice(0, 7) === dateEnd.slice(0, 7)) {
    const monthYear = start.toLocaleDateString(locale, { month: 'long', year: 'numeric' });
    return `${start.getDate()}–${end.getDate()} ${monthYear}`;
  }

  return `${start.toLocaleDateString(locale, opts)} – ${end.toLocaleDateString(locale, opts)}`;
}

/** Full date range: "Monday, January 15, 2026 – Tuesday, January 16, 2026" */
export function formatDateRangeFull(start: string, end: string, lang: string): string {
  const locale = getLocale(lang);
  const opts: Intl.DateTimeFormatOptions = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
  const s = new Date(start + 'T00:00:00');
  const startStr = s.toLocaleDateString(locale, opts);
  if (start === end) return startStr;
  const e = new Date(end + 'T00:00:00');
  const endStr = e.toLocaleDateString(locale, opts);
  return `${startStr} – ${endStr}`;
}

/** Short date range: "Jan 15–16" (same month) or "Jan 15–Feb 3" (different months) */
export function formatDateRangeShort(dateStart: string, dateEnd: string, lang: string): string {
  if (dateStart === dateEnd) return formatDateShort(dateStart, lang);
  const s = new Date(dateStart + 'T00:00:00');
  const e = new Date(dateEnd + 'T00:00:00');
  const locale = getLocale(lang);
  if (dateStart.slice(0, 7) === dateEnd.slice(0, 7)) {
    const month = s.toLocaleDateString(locale, { month: 'short' });
    return `${month} ${s.getDate()}\u2013${e.getDate()}`;
  }
  return `${formatDateShort(dateStart, lang)}\u2013${formatDateShort(dateEnd, lang)}`;
}

/** Date range with year: "Jan 15, 2026 – Jan 18, 2026" or single date if equal/missing */
export function formatDateRangeShortYear(dateStart: string, dateEnd: string, lang: string): string {
  if (!dateEnd || dateStart === dateEnd) return formatDateShortYear(dateStart, lang);
  return `${formatDateShortYear(dateStart, lang)} – ${formatDateShortYear(dateEnd, lang)}`;
}

/** Long day-of-week name from a date string: "Monday" / "Senin" */
export function formatDayLong(dateStr: string, lang: string): string {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString(getLocale(lang), { weekday: 'long' });
}

/** Long weekday + short month + day: "Monday, Jan 15" / locale equivalent */
export function formatDateWithDayLong(dateStr: string, lang: string): string {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString(getLocale(lang), { weekday: 'long', month: 'short', day: 'numeric' });
}

/** Compact date range with weekday: "Mon, Jan 15 – Tue, Jan 16" */
export function formatDateRangeCompact(startDate: string, endDate: string, lang: string): string {
  const locale = getLocale(lang);
  const start = new Date(startDate + 'T00:00:00');
  const end = new Date(endDate + 'T00:00:00');
  const opts: Intl.DateTimeFormatOptions = { weekday: 'short', month: 'short', day: 'numeric' };
  return `${start.toLocaleDateString(locale, opts)} – ${end.toLocaleDateString(locale, opts)}`;
}
